import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { BRAND_LOGO_URL } from "../lib/api";
import {
  LogOut,
  Newspaper,
  AppWindow,
  UserRound,
  Settings,
} from "lucide-react";
import ShiftWidget from "./ShiftWidget";

const NAV_ITEMS = [
  { to: "/feed", label: "Activity", icon: Newspaper, testid: "nav-activity" },
  { to: "/apps", label: "Apps", icon: AppWindow, testid: "nav-apps" },
  { to: "/profile", label: "Profile", icon: UserRound, testid: "nav-profile" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (!user || user === false) return null;

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-border"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link
          to="/feed"
          className="flex items-center gap-2.5 shrink-0"
          data-testid="navbar-logo-link"
        >
          <img
            src={BRAND_LOGO_URL}
            alt="Design Plus"
            className="h-9 w-9 rounded-lg object-cover ring-1 ring-black/5"
          />
          <div className="hidden md:flex flex-col leading-tight">
            <span className="font-display font-bold text-sm dp-gradient-text">
              DESIGN PLUS
            </span>
            <span className="text-[10px] text-muted-foreground -mt-0.5">
              Team Portal
            </span>
          </div>
        </Link>

        <nav className="flex items-center gap-1 flex-1 justify-center">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Button
                key={item.to}
                variant={active ? "secondary" : "ghost"}
                size="sm"
                onClick={() => navigate(item.to)}
                data-testid={item.testid}
                className={active ? "font-semibold" : ""}
              >
                <Icon className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">{item.label}</span>
              </Button>
            );
          })}
          {(user.role === "admin" || user.is_manager) && (
            <Button
              variant={isActive("/admin") ? "secondary" : "ghost"}
              size="sm"
              onClick={() => navigate("/admin")}
              data-testid="nav-admin"
              className={isActive("/admin") ? "font-semibold" : ""}
            >
              <Settings className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">
                {user.role === "admin" ? "Admin" : "Manage"}
              </span>
            </Button>
          )}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <ShiftWidget />

          <div className="hidden lg:flex items-center gap-2 pl-3 border-l border-border">
            <div className="text-right leading-tight">
              <div
                className="text-sm font-medium"
                data-testid="navbar-user-name"
              >
                {user.name || user.email}
              </div>
              <div className="text-[10px] text-muted-foreground capitalize">
                {user.role === "admin"
                  ? "Admin"
                  : user.is_manager
                    ? "Manager"
                    : "Employee"}
              </div>
            </div>
            <div className="h-8 w-8 rounded-full overflow-hidden ring-1 ring-black/5">
              {user.photo ? (
                <img
                  src={user.photo}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full dp-gradient-bg flex items-center justify-center text-white text-xs font-semibold">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            data-testid="navbar-logout-btn"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
